import React, { useEffect, useMemo, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import
{
    ChevronDown,
    PlusLg,
    Trash3,
    PencilSquare,
    XLg,
    ExclamationTriangleFill
} from 'react-bootstrap-icons';
import
{
    fetchUsers,
    updateUser,
    deleteUser,
    previewSelfDeletion,
    deleteCurrentUser,
    createUser,
    bulkDeleteUsers,
    UserListItem,
    SortDirection,
    UserFilterParams,
    OrganizationMembershipRequest,
    SelfDeletionPreview
} from '../api/users';
import { createSuperAdminUser, deleteSuperAdminUser, updateSuperAdminUser } from '../api/superadmin';
import { fetchOrganizations } from '../api/organizations';
import { useAuth } from '../contexts/AuthContext';
import CustomDropdown, { CustomDropdownOption } from '../components/CustomDropdown';
import { passwordPolicyError } from '../utils/passwordPolicy';
import './UserManagement-new.css';

/* ---------- Filter dropdown option lists ---------- */

const ROLE_FILTER_OPTIONS: CustomDropdownOption[] = [
    { value: '', label: 'All Roles' },
    { value: 'user', label: 'User' },
    { value: 'admin', label: 'Admin' },
    { value: 'superadmin', label: 'Super Admin' },
];

/* ============================================================
   UserManagement-new.tsx
   Redesigned admin user management page.
   Table: USERS | ROLE | STATUS | ORGANIZATION | ACTION
   ============================================================ */

/* ---------- Constants ---------- */

/* Number of rows per page */
const PAGE_SIZE = 7;

/* Avatar background-color palette — cycles based on user id */
const AVATAR_COLORS = [
    'um-avatar-blue',
    'um-avatar-teal',
    'um-avatar-purple',
    'um-avatar-orange',
    'um-avatar-pink',
    'um-avatar-green',
    'um-avatar-red',
    'um-avatar-indigo'
];

/* ---------- Type definitions ---------- */

/* Extend the API's UserListItem with fields the backend already sends but the TS type omits */
interface ExtendedUser extends UserListItem
{
    username?: string;
    is_active?: boolean;
    organization_name?: string;
    organization_ids?: number[];
    initial_organization_ids?: number[];
    organization_roles?: Record<number, OrganizationMembershipRole>;
    organizations?: Array<{
        organization_id: number;
        organization_name?: string;
        role?: string;
    }>;
}

interface OrganizationOption
{
    id: number;
    name: string;
}

interface OrganizationMultiSelectProps
{
    options: OrganizationOption[];
    selectedIds: number[];
    onChange: (selectedIds: number[]) => void;
    placeholder?: string;
    invalid?: boolean;
    disabled?: boolean;
}

/* Notification banner */
interface Notification
{
    type: 'success' | 'danger';
    message: string;
}

type OrganizationMembershipRole = 'admin' | 'user';

interface CreateUserForm
{
    first_name: string;
    last_name: string;
    email: string;
    temporary_password: string;
    is_admin: boolean;
    is_superadmin: boolean;
    organization_ids: number[];
    organization_roles: Record<number, OrganizationMembershipRole>;
}

let organizationOptionsRequest: Promise<OrganizationOption[]> | null = null;

async function fetchOrganizationOptions(): Promise<OrganizationOption[]>
{
    if (!organizationOptionsRequest)
    {
        organizationOptionsRequest = fetchOrganizations(1, 200, false, undefined, {
            ignoreActiveOrganizationScope: true,
        })
            .then((data) =>
                data.items
                    .map((org) => ({
                        id: Number(org.id || 0),
                        name: String(org.name || '').trim(),
                    }))
                    .filter((org) => org.id > 0 && !!org.name)
            )
            .finally(() => {
                organizationOptionsRequest = null;
            });
    }

    return organizationOptionsRequest;
}

/* ---------- Helpers ---------- */

/* Get initials from first + last name (max 2 chars) */
function getInitials(firstName: string, lastName: string): string
{
    const f = firstName.trim().charAt(0).toUpperCase();
    const l = lastName.trim().charAt(0).toUpperCase();
    return f + l || f || '?';
}

/* Pick a deterministic avatar color class based on user id */
function getAvatarColor(userId: number): string
{
    return AVATAR_COLORS[userId % AVATAR_COLORS.length];
}

function getUserFullName(user: ExtendedUser): string
{
    const explicitName = String(user.full_name || '').trim();
    if (explicitName)
    {
        return explicitName;
    }

    const combinedName = `${String(user.first_name || '').trim()} ${String(user.last_name || '').trim()}`.trim();
    if (combinedName)
    {
        return combinedName;
    }

    const username = String(user.username || '').trim();
    if (username)
    {
        return username;
    }

    return user.email;
}

/* Map is_admin to a display role */
function getRoleLabel(user: ExtendedUser): string
{
    if (String(user.role || '').trim().toLowerCase() === 'superadmin')
    {
        return 'Super Admin';
    }
    if (user.is_admin)
    {
        return 'Admin';
    }
    return 'User';
}

/* CSS class for the role label */
function getRoleClass(user: ExtendedUser): string
{
    if (String(user.role || '').trim().toLowerCase() === 'superadmin')
    {
        return 'um-role um-role-admin';
    }
    if (user.is_admin)
    {
        return 'um-role um-role-admin';
    }
    return 'um-role um-role-user';
}

function isSuperAdminUser(user: ExtendedUser): boolean
{
    return String(user.role || '').trim().toLowerCase() === 'superadmin';
}

function normalizeMembershipRole(role?: string): OrganizationMembershipRole
{
    return String(role || '').trim().toLowerCase() === 'admin' ? 'admin' : 'user';
}

function reconcileOrganizationRoles(
    selectedIds: number[],
    currentRoles: Record<number, OrganizationMembershipRole> = {},
    defaultRole: OrganizationMembershipRole = 'user'
): Record<number, OrganizationMembershipRole>
{
    return selectedIds.reduce<Record<number, OrganizationMembershipRole>>((nextRoles, orgId) =>
    {
        nextRoles[orgId] = currentRoles[orgId] || defaultRole;
        return nextRoles;
    }, {});
}

function buildOrganizationMemberships(
    organizationIds: number[],
    roleByOrgId: Record<number, OrganizationMembershipRole> = {}
): OrganizationMembershipRequest[]
{
    return organizationIds.map((organizationId) => ({
        organization_id: organizationId,
        role: roleByOrgId[organizationId] || 'user',
    }));
}

/* Map is_active to status label */
function getStatusLabel(user: ExtendedUser): string
{
    if (user.is_active === false)
    {
        return 'Inactive';
    }
    return 'Active';
}

/* CSS class for the status badge */
function getStatusClass(user: ExtendedUser): string
{
    if (user.is_active === false)
    {
        return 'um-status um-status-inactive';
    }
    return 'um-status um-status-active';
}

function OrganizationMultiSelect({
    options,
    selectedIds,
    onChange,
    placeholder = 'Select organizations',
    invalid = false,
    disabled = false,
}: OrganizationMultiSelectProps): React.ReactElement
{
    const [open, setOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement | null>(null);

    useEffect(() =>
    {
        function handleDocumentClick(event: MouseEvent): void
        {
            if (!dropdownRef.current?.contains(event.target as Node))
            {
                setOpen(false);
            }
        }

        document.addEventListener('mousedown', handleDocumentClick);
        return () => document.removeEventListener('mousedown', handleDocumentClick);
    }, []);

    const selectedNames = options
        .filter((option) => selectedIds.includes(option.id))
        .map((option) => option.name);

    let triggerLabel = placeholder;
    if (selectedNames.length === 1)
    {
        triggerLabel = selectedNames[0];
    }
    else if (selectedNames.length > 1)
    {
        triggerLabel = `${selectedNames.length} organizations selected`;
    }

    function handleToggleOption(optionId: number): void
    {
        if (disabled)
        {
            return;
        }

        if (selectedIds.includes(optionId))
        {
            onChange(selectedIds.filter((id) => id !== optionId));
            return;
        }

        onChange([...selectedIds, optionId]);
    }

    return (
        <div className="um-org-select-wrap" ref={dropdownRef}>
            <button
                type="button"
                className={`um-org-select-trigger${invalid ? ' is-invalid' : ''}`}
                onClick={() => !disabled && setOpen((prev) => !prev)}
                disabled={disabled}
            >
                <span className={`um-org-select-value${selectedNames.length > 0 ? ' has-value' : ''}`}>
                    {triggerLabel}
                </span>
                <ChevronDown className={`um-org-select-chevron${open ? ' open' : ''}`} />
            </button>

            {open && (
                <div className="um-org-select-menu">
                    {options.map((option) =>
                    {
                        const selected = selectedIds.includes(option.id);
                        return (
                            <button
                                key={option.id}
                                type="button"
                                className={`um-org-select-option${selected ? ' selected' : ''}`}
                                onClick={() => handleToggleOption(option.id)}
                            >
                                <span className={`um-org-select-check${selected ? ' selected' : ''}`} aria-hidden="true">
                                    <span className="um-org-select-check-dot" />
                                </span>
                                <span className="um-org-select-option-copy">
                                    <span className="um-org-select-option-title">{option.name}</span>
                                    {selected && <span className="um-org-select-option-meta">Selected</span>}
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

/* ============================================================
   Main Component
   ============================================================ */

const UserManagement: React.FC = () =>
{
    const { user, logout } = useAuth();
    const isSuperAdmin = Boolean(user && /superadmin/i.test(user.role || ''));
    const activeScopedOrgId = Number(user?.active_organization_id || user?.organization_id || 0) || null;
    const hasActiveOrganizationScope = isSuperAdmin && activeScopedOrgId !== null;

    /* ---------- State ---------- */

    const [items, setItems] = useState<ExtendedUser[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    /* Search / filter state — "input" holds what the user types; "applied" drives the API call */
    const [searchInput, setSearchInput] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [roleInput, setRoleInput] = useState('');
    const [appliedRole, setAppliedRole] = useState('');
    const [fromDateInput, setFromDateInput] = useState('');
    const [appliedFromDate, setAppliedFromDate] = useState('');
    const [toDateInput, setToDateInput] = useState('');
    const [appliedToDate, setAppliedToDate] = useState('');
    const [dateError, setDateError] = useState('');
    /* Org filter — super admin only */
    const [orgFilterInput, setOrgFilterInput] = useState('');
    const [appliedOrgFilter, setAppliedOrgFilter] = useState('');

    /* Edit modal */
    const [editTarget, setEditTarget] = useState<ExtendedUser | null>(null);

    /* Delete modal */
    const [deleteTarget, setDeleteTarget] = useState<ExtendedUser | null>(null);
    const [selfDeletePreview, setSelfDeletePreview] = useState<SelfDeletionPreview | null>(null);
    const [selfDeletePreviewLoading, setSelfDeletePreviewLoading] = useState(false);

    /* Create modal */
    const [showCreate, setShowCreate] = useState(false);
    const [createForm, setCreateForm] = useState<CreateUserForm>({
        first_name: '',
        last_name: '',
        email: '',
        temporary_password: '',
        is_admin: false,
        is_superadmin: false,
        organization_ids: [] as number[],
        organization_roles: {} as Record<number, OrganizationMembershipRole>
    });
    const [createErrors, setCreateErrors] = useState<{ [k: string]: string }>({});

    /* Bulk delete confirmation modal */
    const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);

    /* Delete mode — toggles the header and banner for bulk selection UI */
    const [deleteMode, setDeleteMode] = useState(false);

    /* Password visibility toggle in create modal */
    const [showCreatePassword, setShowCreatePassword] = useState(false);

    /* Shared flags */
    const [saving, setSaving] = useState(false);
    const [bulkRunning, setBulkRunning] = useState(false);
    const [notify, setNotify] = useState<Notification | null>(null);
    const [currentUserEmail, setCurrentUserEmail] = useState<string | null>(null);
    const [currentOrgId, setCurrentOrgId] = useState<number | null>(null);
    const [organizationOptions, setOrganizationOptions] = useState<OrganizationOption[]>([]);
    const [organizationNameById, setOrganizationNameById] = useState<Record<number, string>>({});
    const [selectedIds, setSelectedIds] = useState<number[]>([]);

    function buildDefaultCreateForm(): CreateUserForm
    {
        const defaultOrganizationIds = currentOrgId ? [currentOrgId] : [];
        return {
            first_name: '',
            last_name: '',
            email: '',
            temporary_password: '',
            is_admin: false,
            is_superadmin: false,
            organization_ids: defaultOrganizationIds,
            organization_roles: reconcileOrganizationRoles(defaultOrganizationIds)
        };
    }

    function resetCreateForm(): void
    {
        setCreateForm(buildDefaultCreateForm());
        setCreateErrors({});
        setShowCreatePassword(false);
    }

    /* Computed total pages */
    const totalPages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);

    /* ---------- Data loading ---------- */

    /* Fetch users from the API */
    async function loadUsers(): Promise<void>
    {
        setLoading(true);
        setError(null);
        try
        {
            const sortBy = 'created_at';
            const sortDir: SortDirection = 'desc';
            const filters: UserFilterParams = {};
            if (appliedSearch) filters.search = appliedSearch;
            if (appliedRole) filters.role = appliedRole;
            if (appliedFromDate) filters.date_from = appliedFromDate;
            if (appliedToDate) filters.date_to = appliedToDate;
            if (isSuperAdmin && !hasActiveOrganizationScope && appliedOrgFilter) filters.organization_id = Number(appliedOrgFilter);
            const data = await fetchUsers(page, PAGE_SIZE, sortBy, sortDir, filters);
            setItems(data.items as ExtendedUser[]);
            setTotal(data.total);
        }
        catch (err: any)
        {
            setError(err.response?.data?.detail || 'Failed to load users');
        }
        finally
        {
            setLoading(false);
        }
    }

    /* Fetch available organizations for the modal dropdowns */
    async function loadOrganizationOptions(): Promise<void>
    {
        try
        {
            const options = await fetchOrganizationOptions();

            const orgNameMap = options.reduce<Record<number, string>>((acc, org) => {
                const orgId = Number(org.id || 0);
                if (orgId && org.name)
                {
                    acc[orgId] = org.name;
                }
                return acc;
            }, {});

            setOrganizationNameById(orgNameMap);
            setOrganizationOptions(options);

            setCreateForm((prev) => {
                if (prev.organization_ids.length > 0 || !currentOrgId)
                {
                    return prev;
                }
                return {
                    ...prev,
                    organization_ids: orgNameMap[currentOrgId] ? [currentOrgId] : prev.organization_ids,
                    organization_roles: orgNameMap[currentOrgId]
                        ? reconcileOrganizationRoles([currentOrgId], prev.organization_roles)
                        : prev.organization_roles,
                };
            });
        }
        catch
        {
            setOrganizationNameById({});
            setOrganizationOptions([]);
        }
    }

    function getUserOrganizationIds(user: ExtendedUser): number[]
    {
        if (Array.isArray(user.organization_ids) && user.organization_ids.length > 0)
        {
            return Array.from(new Set(user.organization_ids.map((orgId) => Number(orgId || 0)).filter((orgId) => orgId > 0)));
        }

        if (Array.isArray(user.organizations) && user.organizations.length > 0)
        {
            return Array.from(new Set(
                user.organizations
                    .map((membership) => Number(membership?.organization_id || 0))
                    .filter((orgId) => orgId > 0)
            ));
        }

        return [];
    }

    function getUserOrganizationRoleMap(user: ExtendedUser): Record<number, OrganizationMembershipRole>
    {
        const roleMap: Record<number, OrganizationMembershipRole> = {};

        if (user.organization_roles)
        {
            Object.entries(user.organization_roles).forEach(([orgId, role]) =>
            {
                const parsedOrgId = Number(orgId || 0);
                if (parsedOrgId > 0)
                {
                    roleMap[parsedOrgId] = normalizeMembershipRole(role);
                }
            });
        }

        if (Array.isArray(user.organizations))
        {
            user.organizations.forEach((membership) =>
            {
                const orgId = Number(membership?.organization_id || 0);
                if (orgId > 0)
                {
                    roleMap[orgId] = normalizeMembershipRole(membership?.role);
                }
            });
        }

        getUserOrganizationIds(user).forEach((orgId) =>
        {
            if (!roleMap[orgId])
            {
                roleMap[orgId] = user.is_admin ? 'admin' : 'user';
            }
        });

        return roleMap;
    }

    function getUserOrganizationNames(user: ExtendedUser): string[]
    {
        if (Array.isArray(user.organizations) && user.organizations.length > 0)
        {
            const names = user.organizations
                .map((membership) => String(membership?.organization_name || '').trim())
                .filter((name) => !!name);

            if (names.length > 0)
            {
                return Array.from(new Set(names));
            }
        }

        const directName = String(user.organization_name || '').trim();
        if (directName)
        {
            return [directName];
        }

        if (Array.isArray(user.organization_ids) && user.organization_ids.length > 0)
        {
            const names = user.organization_ids
                .map((orgId) => organizationNameById[Number(orgId)])
                .filter((name): name is string => Boolean(name && name.trim()));

            if (names.length > 0)
            {
                return Array.from(new Set(names));
            }
        }

        return [];
    }

    /* Reload when page or applied filters change */
    useEffect(() =>
    {
        loadUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, appliedSearch, appliedRole, appliedFromDate, appliedToDate, appliedOrgFilter, hasActiveOrganizationScope]);

    /* Read current user email from localStorage */
    useEffect(() =>
    {
        setCurrentUserEmail(user?.email || null);
        setCurrentOrgId(activeScopedOrgId);
        if (activeScopedOrgId)
        {
            setCreateForm((prev) => ({
                ...prev,
                organization_ids: [activeScopedOrgId],
                organization_roles: reconcileOrganizationRoles([activeScopedOrgId], prev.organization_roles),
            }));
        }
    }, [activeScopedOrgId, user]);

    useEffect(() =>
    {
        if (!hasActiveOrganizationScope)
        {
            return;
        }
        setOrgFilterInput('');
        setAppliedOrgFilter('');
    }, [hasActiveOrganizationScope]);

    useEffect(() =>
    {
        if (hasActiveOrganizationScope && !currentOrgId)
        {
            return;
        }
        loadOrganizationOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentOrgId, hasActiveOrganizationScope]);

    /* ---------- Selection helpers ---------- */

    /* Whether a user can be selected for bulk delete */
    function isSelectable(u: ExtendedUser): boolean
    {
        if (currentUserEmail && currentUserEmail.toLowerCase() === u.email.toLowerCase())
        {
            return false;
        }
        return true;
    }

    /* Toggle all checkboxes */
    function handleToggleSelectAll(checked: boolean): void
    {
        if (checked)
        {
            setSelectedIds(items.filter(isSelectable).map((i) => i.id));
        }
        else
        {
            setSelectedIds([]);
        }
    }

    /* Toggle a single checkbox */
    function handleToggleSelectOne(id: number, checked: boolean): void
    {
        setSelectedIds((prev) =>
            checked
                ? Array.from(new Set([...prev, id]))
                : prev.filter((x) => x !== id)
        );
    }

    /* ---------- Create user ---------- */

    /* Validate create form fields */
    function validateCreate(): boolean
    {
        const errs: { [k: string]: string } = {};

        if (!createForm.first_name.trim())
        {
            errs.first_name = 'First name is required';
        }
        if (!createForm.last_name.trim())
        {
            errs.last_name = 'Last name is required';
        }
        if (!createForm.email.trim())
        {
            errs.email = 'Email is required';
        }
        else
        {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(createForm.email))
            {
                errs.email = 'Invalid email format';
            }
        }
        if (!createForm.temporary_password)
        {
            errs.temporary_password = 'Temporary password is required';
        }
        else
        {
            const fullName = `${createForm.first_name} ${createForm.last_name}`.trim();
            const policyError = passwordPolicyError(createForm.temporary_password, [createForm.email, fullName]);
            if (policyError)
            {
                errs.temporary_password = policyError;
            }
        }
        if (!createForm.is_superadmin && createForm.organization_ids.length === 0)
        {
            errs.organization_ids = 'Select at least one organization';
        }

        setCreateErrors(errs);
        return Object.keys(errs).length === 0;
    }

    /* Submit create form */
    async function handleCreate(): Promise<void>
    {
        if (!validateCreate())
        {
            return;
        }
        setSaving(true);
        try
        {
            if (createForm.is_superadmin)
            {
                await createSuperAdminUser({
                    email: createForm.email,
                    username: createForm.email.split('@')[0],
                    full_name: `${createForm.first_name} ${createForm.last_name}`.trim(),
                    password: createForm.temporary_password,
                });
            }
            else
            {
                const organizationMemberships = buildOrganizationMemberships(
                    createForm.organization_ids,
                    createForm.organization_roles
                );
                await createUser({
                    ...createForm,
                    organization_ids: createForm.organization_ids,
                    organization_memberships: organizationMemberships,
                    is_admin: organizationMemberships.some((membership) => membership.role === 'admin'),
                } as any);
            }
            setNotify({ type: 'success', message: 'User created successfully' });
            setShowCreate(false);
            resetCreateForm();
            await loadUsers();
        }
        catch (err: any)
        {
            setNotify({ type: 'danger', message: err.response?.data?.detail || err.message || 'Failed to create user' });
        }
        finally
        {
            setSaving(false);
        }
    }

    /* Auto-generate a secure temporary password */
    function handleAutoGeneratePassword(): void
    {
        const groups = [
            'ABCDEFGHJKLMNPQRSTUVWXYZ',
            'abcdefghijkmnopqrstuvwxyz',
            '23456789',
            '!@$%*?-',
        ];
        const chars = groups.join('');
        const passwordChars = groups.map((group) => group.charAt(Math.floor(Math.random() * group.length)));
        while (passwordChars.length < 16)
        {
            passwordChars.push(chars.charAt(Math.floor(Math.random() * chars.length)));
        }
        for (let i = passwordChars.length - 1; i > 0; i--)
        {
            const swapIndex = Math.floor(Math.random() * (i + 1));
            [passwordChars[i], passwordChars[swapIndex]] = [passwordChars[swapIndex], passwordChars[i]];
        }
        const pwd = passwordChars.join('');
        setCreateForm({ ...createForm, temporary_password: pwd });
    }

    function handleCreateOrganizationIdsChange(organizationIds: number[]): void
    {
        setCreateForm((prev) => ({
            ...prev,
            organization_ids: organizationIds,
            organization_roles: reconcileOrganizationRoles(organizationIds, prev.organization_roles),
        }));
    }

    function handleCreateOrganizationRoleChange(organizationId: number, role: OrganizationMembershipRole): void
    {
        setCreateForm((prev) => ({
            ...prev,
            organization_roles: {
                ...prev.organization_roles,
                [organizationId]: role,
            },
        }));
    }

    function handleEditOrganizationIdsChange(organizationIds: number[]): void
    {
        setEditTarget((prev) =>
            prev
                ? {
                    ...prev,
                    organization_ids: organizationIds,
                    organization_roles: reconcileOrganizationRoles(
                        organizationIds,
                        prev.organization_roles || getUserOrganizationRoleMap(prev)
                    ),
                }
                : prev
        );
    }

    function handleEditOrganizationRoleChange(organizationId: number, role: OrganizationMembershipRole): void
    {
        setEditTarget((prev) =>
            prev
                ? {
                    ...prev,
                    organization_roles: {
                        ...(prev.organization_roles || getUserOrganizationRoleMap(prev)),
                        [organizationId]: role,
                    },
                }
                : prev
        );
    }

    function renderOrganizationRoleRows(
        organizationIds: number[],
        roleByOrgId: Record<number, OrganizationMembershipRole>,
        onRoleChange: (organizationId: number, role: OrganizationMembershipRole) => void
    ): React.ReactNode
    {
        if (organizationIds.length === 0)
        {
            return null;
        }

        return (
            <div className="um-org-role-list">
                {organizationIds.map((organizationId) => (
                    <div className="um-org-role-row" key={`org-role-${organizationId}`}>
                        <div className="um-org-role-name">
                            {organizationNameById[organizationId] || `Organization ${organizationId}`}
                        </div>
                        <Form.Select
                            className="um-org-role-select"
                            value={roleByOrgId[organizationId] || 'user'}
                            onChange={(event) =>
                                onRoleChange(organizationId, normalizeMembershipRole(event.currentTarget.value))}
                            aria-label={`Role for ${organizationNameById[organizationId] || `organization ${organizationId}`}`}
                        >
                            <option value="user">User</option>
                            <option value="admin">Admin</option>
                        </Form.Select>
                    </div>
                ))}
            </div>
        );
    }

    /* ---------- Edit user ---------- */

    /* Save edited user */
    async function handleSaveEdit(): Promise<void>
    {
        if (!editTarget)
        {
            return;
        }
        setSaving(true);
        try
        {
            if (isSuperAdminUser(editTarget))
            {
                await updateSuperAdminUser(editTarget.id, {
                    username: editTarget.username,
                    full_name: `${editTarget.first_name} ${editTarget.last_name}`.trim(),
                });
                setEditTarget(null);
                await loadUsers();
                return;
            }

            const organizationIds = getUserOrganizationIds(editTarget);
            const initialOrganizationIds = Array.isArray(editTarget.initial_organization_ids)
                ? editTarget.initial_organization_ids
                : organizationIds;

            if (organizationIds.length === 0)
            {
                throw new Error('Select at least one organization');
            }

            await updateUser(editTarget.id, {
                first_name: editTarget.first_name,
                last_name: editTarget.last_name,
                email: editTarget.email,
                organization_ids: organizationIds,
                organization_memberships: buildOrganizationMemberships(
                    organizationIds,
                    editTarget.organization_roles || getUserOrganizationRoleMap(editTarget)
                ),
                current_organization_ids: initialOrganizationIds,
            } as any);
            setEditTarget(null);
            await loadUsers();
        }
        catch (err: any)
        {
            setNotify({ type: 'danger', message: err.response?.data?.detail || 'Failed to update user' });
        }
        finally
        {
            setSaving(false);
        }
    }

    /* ---------- Delete user ---------- */

    async function openDeleteModal(userTarget: ExtendedUser): Promise<void>
    {
        setDeleteTarget(userTarget);
        setSelfDeletePreview(null);
        const isSelf = Boolean(currentUserEmail && currentUserEmail.toLowerCase() === userTarget.email.toLowerCase());
        if (!isSelf)
        {
            return;
        }

        setSelfDeletePreviewLoading(true);
        try
        {
            setSelfDeletePreview(await previewSelfDeletion());
        }
        catch (err: any)
        {
            setNotify({ type: 'danger', message: err.response?.data?.detail || 'Failed to inspect account deletion impact' });
            setDeleteTarget(null);
        }
        finally
        {
            setSelfDeletePreviewLoading(false);
        }
    }

    /* Confirm single delete */
    async function handleConfirmDelete(): Promise<void>
    {
        if (!deleteTarget)
        {
            return;
        }
        setSaving(true);
        try
        {
            const isSelf = Boolean(currentUserEmail && currentUserEmail.toLowerCase() === deleteTarget.email.toLowerCase());
            if (isSelf)
            {
                if (selfDeletePreviewLoading || !selfDeletePreview)
                {
                    return;
                }
                if (selfDeletePreview.running_jobs.length > 0)
                {
                    const jobIds = selfDeletePreview.running_jobs.map((job) => job.job_id).join(', ');
                    setNotify({ type: 'danger', message: `Your organization jobs are active (${jobIds}). Cancel or stop them before deleting your account.` });
                    return;
                }
                await deleteCurrentUser();
                setDeleteTarget(null);
                logout();
                return;
            }

            if (isSuperAdminUser(deleteTarget))
            {
                await deleteSuperAdminUser(deleteTarget.id);
            }
            else
            {
                await deleteUser(deleteTarget.id);
            }
            setNotify({ type: 'success', message: 'User deleted successfully' });
            setDeleteTarget(null);
            await loadUsers();
        }
        catch (err: any)
        {
            setNotify({ type: 'danger', message: err.response?.data?.detail || 'Failed to delete user' });
        }
        finally
        {
            setSaving(false);
        }
    }

    /* ---------- Bulk delete ---------- */

    /* Bulk delete selected users — opens confirmation modal */
    function handleBulkDeleteClick(): void
    {
        if (selectedIds.length === 0)
        {
            return;
        }
        setShowBulkDeleteModal(true);
    }

    /* Confirm bulk delete from modal */
    async function handleConfirmBulkDelete(): Promise<void>
    {
        setBulkRunning(true);
        try
        {
            await bulkDeleteUsers(selectedIds);
            setNotify({ type: 'success', message: `Deleted ${selectedIds.length} users` });
            setSelectedIds([]);
            setShowBulkDeleteModal(false);
            setDeleteMode(false);
            await loadUsers();
        }
        catch (err: any)
        {
            setNotify({ type: 'danger', message: err.response?.data?.detail || 'Bulk delete failed' });
        }
        finally
        {
            setBulkRunning(false);
        }
    }

    /* Enter delete mode */
    function handleEnterDeleteMode(): void
    {
        setDeleteMode(true);
        setSelectedIds([]);
    }

    /* Exit delete mode */
    function handleExitDeleteMode(): void
    {
        setDeleteMode(false);
        setSelectedIds([]);
    }

    /* ---------- Filter handlers ---------- */

    /* Apply current input values → commits them as applied filters and resets to page 1 */
    function applyFilters(): void
    {
        if ((fromDateInput && !toDateInput) || (!fromDateInput && toDateInput))
        {
            setDateError('Please select both a start date and an end date.');
            return;
        }
        setDateError('');
        setAppliedSearch(searchInput);
        setAppliedRole(roleInput);
        setAppliedFromDate(fromDateInput);
        setAppliedToDate(toDateInput);
        setAppliedOrgFilter(orgFilterInput);
        setPage(1);
    }

    /* Allow pressing Enter in the search box to trigger search */
    function handleSearchKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void
    {
        if (e.key === 'Enter')
        {
            applyFilters();
        }
    }

    function handleClearFilters(): void
    {
        setSearchInput('');
        setRoleInput('');
        setFromDateInput('');
        setToDateInput('');
        setAppliedSearch('');
        setAppliedRole('');
        setAppliedFromDate('');
        setAppliedToDate('');
        setAppliedOrgFilter('');
        setOrgFilterInput('');
        setDateError('');
        setPage(1);
    }

    /* ---------- Render helpers ---------- */

    /* Render the search + filter bar */
    function renderFilters(): React.ReactNode
    {
        const hasActiveFilters = !!(appliedSearch || appliedRole || appliedFromDate || appliedToDate || appliedOrgFilter);

        return (
            <div className="um-filters">
                {/* Search by name / email */}
                <div className="um-filter-field um-filter-search">
                    <svg className="um-filter-search-icon" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                        <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.8" />
                        <path d="M13.5 13.5L17 17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    </svg>
                    <input
                        className="um-filter-input um-filter-input-search"
                        type="text"
                        placeholder="Search by name or email…"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        onKeyDown={handleSearchKeyDown}
                    />
                </div>

                {/* Organization filter — super admin only */}
                {isSuperAdmin && !hasActiveOrganizationScope && (
                    <div className="um-filter-field um-filter-dropdown">
                        <CustomDropdown
                            value={orgFilterInput}
                            options={[
                                { value: '', label: 'All Organizations' },
                                ...organizationOptions.map((o) => ({ value: String(o.id), label: o.name })),
                            ]}
                            onChange={(v) => setOrgFilterInput(v)}
                            placeholder="All Organizations"
                        />
                    </div>
                )}

                {/* Role filter */}
                <div className="um-filter-field um-filter-dropdown">
                    <CustomDropdown
                        value={roleInput}
                        options={ROLE_FILTER_OPTIONS}
                        onChange={(v) => setRoleInput(v)}
                        placeholder="All Roles"
                    />
                </div>

                {/* Date range filter */}
                <div className="um-filter-field um-filter-date filter-date-ph-wrap">
                    <input
                        className={`um-filter-date-input${!fromDateInput ? ' date-empty' : ''}${dateError ? ' um-filter-date-input--error' : ''}`}
                        type="date"
                        aria-label="From date"
                        value={fromDateInput}
                        onChange={(e) => { setFromDateInput(e.target.value); setDateError(''); }}
                    />
                    {!fromDateInput && <span className="filter-date-ph" aria-hidden="true">Start Date</span>}
                </div>
                <div className="um-filter-field um-filter-date filter-date-ph-wrap">
                    <input
                        className={`um-filter-date-input${!toDateInput ? ' date-empty' : ''}${dateError ? ' um-filter-date-input--error' : ''}`}
                        type="date"
                        aria-label="To date"
                        value={toDateInput}
                        onChange={(e) => { setToDateInput(e.target.value); setDateError(''); }}
                    />
                    {!toDateInput && <span className="filter-date-ph" aria-hidden="true">End Date</span>}
                </div>
                {dateError && <span className="um-filter-date-error">{dateError}</span>}

                {/* Search button — commits the inputs and fires the API call */}
                <button
                    className="um-btn-search"
                    onClick={applyFilters}
                    type="button"
                    disabled={loading}
                >
                    Search
                </button>

                {/* Clear filters — only shown when applied filters are active */}
                {hasActiveFilters && (
                    <button
                        className="um-btn-clear-filters"
                        onClick={handleClearFilters}
                        type="button"
                    >
                        Clear
                    </button>
                )}
            </div>
        );
    }

    /* Render the notification banner */
    function renderNotification(): React.ReactNode
    {
        if (!notify)
        {
            return null;
        }

        const alertClass = notify.type === 'success' ? 'um-alert um-alert-success' : 'um-alert um-alert-danger';

        return (
            <div className={alertClass}>
                <span>{notify.message}</span>
                <button
                    className="um-alert-dismiss"
                    onClick={() => setNotify(null)}
                    type="button"
                >
                    Dismiss
                </button>
            </div>
        );
    }

    /* Render the page header with title + action buttons */
    function renderHeader(): React.ReactNode
    {
        return (
            <div className="um-header">
                <h1 className="um-title">User Management</h1>
                <div className="um-header-actions">
                    {deleteMode ? (
                        <>
                            {/* Cancel delete mode */}
                            <button
                                className="um-btn-cancel-mode"
                                onClick={handleExitDeleteMode}
                                type="button"
                            >
                                <XLg size={14} />
                                Cancel
                            </button>

                            {/* Delete Selected */}
                            <button
                                className="um-btn-delete-selected"
                                onClick={handleBulkDeleteClick}
                                disabled={selectedIds.length === 0 || bulkRunning}
                                type="button"
                            >
                                <Trash3 size={14} />
                                {bulkRunning ? 'Deleting...' : `Delete Selected (${selectedIds.length})`}
                            </button>
                        </>
                    ) : (
                        <>
                            {/* Create New User */}
                            <button
                                className="um-btn-create"
                                onClick={() => setShowCreate(true)}
                                type="button"
                            >
                                <PlusLg size={16} />
                                Create New User
                            </button>

                            {/* Bulk Delete — enters delete mode */}
                            <button
                                className="um-btn-bulk-delete"
                                onClick={handleEnterDeleteMode}
                                type="button"
                            >
                                <Trash3 size={14} />
                                Bulk Delete ({selectedIds.length})
                            </button>
                        </>
                    )}
                </div>
            </div>
        );
    }

    /* Render the delete mode warning banner */
    function renderDeleteBanner(): React.ReactNode
    {
        if (!deleteMode)
        {
            return null;
        }

        /* Count of selectable users */
        const selectableCount = items.filter(isSelectable).length;
        const bannerText = selectedIds.length === 0
            ? 'Select users to delete'
            : `${selectedIds.length} users selected`;

        return (
            <div className="um-delete-banner">
                <div className="um-delete-banner-left">
                    <ExclamationTriangleFill className="um-delete-banner-icon" />
                    <span>{bannerText}</span>
                </div>
                <button
                    className="um-select-all-link"
                    onClick={() => handleToggleSelectAll(true)}
                    type="button"
                >
                    Select All ({selectableCount})
                </button>
            </div>
        );
    }

    /* Render the table header row */
    function renderTableHead(): React.ReactNode
    {
        return (
            <thead>
                <tr>
                    <th className="um-checkbox-col">
                        <Form.Check
                            type="checkbox"
                            aria-label="Select all"
                            checked={selectedIds.length > 0 && selectedIds.length === items.filter(isSelectable).length}
                            onChange={(e) => handleToggleSelectAll(e.currentTarget.checked)}
                        />
                    </th>
                    <th>Users</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Organization</th>
                    {deleteMode && <th>Team</th>}
                    <th>Action</th>
                </tr>
            </thead>
        );
    }

    function openEditUser(user: ExtendedUser): void
    {
        setEditTarget({
            ...user,
            organization_ids: getUserOrganizationIds(user),
            initial_organization_ids: getUserOrganizationIds(user),
            organization_roles: getUserOrganizationRoleMap(user),
        });
    }

    /* Render a single table row */
    function renderUserRow(user: ExtendedUser): React.ReactNode
    {
        const initials = getInitials(user.first_name, user.last_name);
        const avatarColor = getAvatarColor(user.id);
        const fullName = getUserFullName(user);
        const isSelf = Boolean(currentUserEmail && currentUserEmail.toLowerCase() === user.email.toLowerCase());
        const canDelete = isSelectable(user) || (isSelf && user.is_admin && !isSuperAdmin);
        const organizationNames = getUserOrganizationNames(user);
        const organizationSummary = organizationNames.length <= 1
            ? (organizationNames[0] || '—')
            : `${organizationNames[0]} +${organizationNames.length - 1}`;

        return (
            <tr key={user.id}>
                {/* Checkbox */}
                <td>
                    <Form.Check
                        type="checkbox"
                        aria-label={`Select ${user.email}`}
                        checked={selectedIds.includes(user.id)}
                        disabled={!isSelectable(user)}
                        onChange={(e) => handleToggleSelectOne(user.id, e.currentTarget.checked)}
                    />
                </td>

                {/* User: avatar + name + email */}
                <td>
                    <div className="um-user-cell">
                        <div className={`um-avatar ${avatarColor}`}>
                            {initials}
                        </div>
                        <div>
                            <div className="um-user-name">{fullName}</div>
                            <div className="um-user-email">{user.email}</div>
                        </div>
                    </div>
                </td>

                {/* Role */}
                <td>
                    <span className={getRoleClass(user)}>
                        {getRoleLabel(user)}
                    </span>
                </td>

                {/* Status */}
                <td>
                    <span className={getStatusClass(user)}>
                        {getStatusLabel(user)}
                    </span>
                </td>

                {/* Organization */}
                <td>
                    <div className="um-org-cell">
                        <span className="um-org">{organizationSummary}</span>
                        {organizationNames.length > 0 && (
                            <div className="um-org-hover-card">
                                <div className="um-org-hover-title">Organizations</div>
                                <ul className="um-org-hover-list">
                                    {organizationNames.map((organizationName) => (
                                        <li key={`${user.id}-${organizationName}`} className="um-org-hover-item">
                                            {organizationName}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </td>

                {/* Team — only in delete mode */}
                {deleteMode && (
                    <td>
                        <span className="um-org">—</span>
                    </td>
                )}

                {/* Actions */}
                <td>
                    <div className="um-actions">
                        <button
                            className="um-btn-edit"
                            onClick={() => openEditUser(user)}
                            title="Edit user"
                            type="button"
                        >
                            <PencilSquare size={14} />
                            Edit
                        </button>
                        <button
                            className="um-btn-remove"
                            onClick={() => void openDeleteModal(user)}
                            disabled={!canDelete}
                            title={
                                !canDelete
                                    ? (isSelf ? 'Only organization admins can delete their own account' : 'You cannot delete this account')
                                    : 'Remove user'
                            }
                            type="button"
                        >
                            <Trash3 size={14} />
                            Remove
                        </button>
                    </div>
                </td>
            </tr>
        );
    }

    function renderMobileUserCard(user: ExtendedUser): React.ReactNode
    {
        const initials = getInitials(user.first_name, user.last_name);
        const avatarColor = getAvatarColor(user.id);
        const fullName = getUserFullName(user);
        const canDelete = isSelectable(user);
        const organizationNames = getUserOrganizationNames(user);
        const selected = selectedIds.includes(user.id);

        return (
            <article
                className={`um-mobile-user-card${selected ? ' is-selected' : ''}`}
                key={user.id}
                role="listitem"
            >
                <div className="um-mobile-user-card-header">
                    {deleteMode && (
                        <Form.Check
                            className="um-mobile-user-select"
                            type="checkbox"
                            aria-label={`Select ${user.email}`}
                            checked={selected}
                            disabled={!canDelete}
                            onChange={(event) => handleToggleSelectOne(user.id, event.currentTarget.checked)}
                        />
                    )}
                    <div className="um-user-cell um-mobile-user-identity">
                        <div className={`um-avatar ${avatarColor}`} aria-hidden="true">
                            {initials}
                        </div>
                        <div className="um-mobile-user-copy">
                            <div className="um-user-name">{fullName}</div>
                            <div className="um-user-email">{user.email}</div>
                        </div>
                    </div>
                </div>

                <div className="um-mobile-user-badges" aria-label="Role and status">
                    <span className={getRoleClass(user)}>{getRoleLabel(user)}</span>
                    <span className={getStatusClass(user)}>{getStatusLabel(user)}</span>
                </div>

                <dl className="um-mobile-user-details">
                    <div>
                        <dt>Organization</dt>
                        <dd>{organizationNames.length > 0 ? organizationNames.join(', ') : '—'}</dd>
                    </div>
                    {deleteMode && (
                        <div>
                            <dt>Team</dt>
                            <dd>—</dd>
                        </div>
                    )}
                </dl>

                <div className="um-actions um-mobile-user-actions">
                    <button
                        className="um-btn-edit"
                        onClick={() => openEditUser(user)}
                        title="Edit user"
                        aria-label={`Edit ${fullName}`}
                        type="button"
                    >
                        <PencilSquare size={14} />
                        Edit
                    </button>
                    <button
                        className="um-btn-remove"
                        onClick={() => setDeleteTarget(user)}
                        disabled={!canDelete}
                        title={!canDelete ? 'You cannot delete your own account' : 'Remove user'}
                        aria-label={`Remove ${fullName}`}
                        type="button"
                    >
                        <Trash3 size={14} />
                        Remove
                    </button>
                </div>
            </article>
        );
    }

    function renderMobileUserList(): React.ReactNode
    {
        if (loading)
        {
            return (
                <div className="um-mobile-list-state" role="status" aria-live="polite">
                    <Spinner animation="border" />
                    <span>Loading users…</span>
                </div>
            );
        }

        if (items.length === 0)
        {
            return <div className="um-mobile-list-state">No users found</div>;
        }

        return (
            <div className="um-mobile-user-list" role="list" aria-label="Users">
                {items.map((user) => renderMobileUserCard(user))}
            </div>
        );
    }

    /* Render the table body */
    function renderTableBody(): React.ReactNode
    {
        /* Column count changes when in delete mode (adds TEAM) */
        const colCount = deleteMode ? 7 : 6;

        if (loading)
        {
            return (
                <tbody>
                    <tr className="um-loading-row">
                        <td colSpan={colCount}>
                            <Spinner animation="border" />
                        </td>
                    </tr>
                </tbody>
            );
        }

        if (items.length === 0)
        {
            return (
                <tbody>
                    <tr className="um-empty-row">
                        <td colSpan={colCount}>No users found</td>
                    </tr>
                </tbody>
            );
        }

        return (
            <tbody>
                {items.map((user) => renderUserRow(user))}
            </tbody>
        );
    }

    /* Render the footer with "Showing X-Y of Z users" and pagination buttons */
    function renderFooter(): React.ReactNode
    {
        /* Calculate the range being displayed */
        const startItem = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
        const endItem = Math.min(page * PAGE_SIZE, total);

        /* Build visible page numbers (up to 5 around current) */
        const pageStart = Math.max(1, page - 2);
        const pageEnd = Math.min(totalPages, pageStart + 4);

        const pageNumbers: number[] = [];
        for (let p = pageStart; p <= pageEnd; p++)
        {
            pageNumbers.push(p);
        }

        return (
            <div className="um-footer">
                {/* Showing text */}
                <span className="um-showing-text">
                    Showing {startItem}–{endItem} of {total} users
                </span>

                {/* Pagination buttons */}
                <div className="um-pagination">
                    {/* First */}
                    <button
                        className="um-page-btn"
                        onClick={() => setPage(1)}
                        disabled={page === 1}
                        type="button"
                    >
                        ← First
                    </button>

                    {/* Prev */}
                    <button
                        className="um-page-btn"
                        onClick={() => setPage(Math.max(1, page - 1))}
                        disabled={page === 1}
                        type="button"
                    >
                        Prev
                    </button>

                    {/* Page numbers */}
                    {pageNumbers.map((p) => (
                        <button
                            key={p}
                            className={`um-page-btn ${p === page ? 'um-page-active' : ''}`}
                            onClick={() => setPage(p)}
                            type="button"
                        >
                            {p}
                        </button>
                    ))}

                    {/* Next */}
                    <button
                        className="um-page-btn"
                        onClick={() => setPage(Math.min(totalPages, page + 1))}
                        disabled={page === totalPages}
                        type="button"
                    >
                        Next
                    </button>

                    {/* Last */}
                    <button
                        className="um-page-btn"
                        onClick={() => setPage(totalPages)}
                        disabled={page === totalPages}
                        type="button"
                    >
                        Last →
                    </button>
                </div>
            </div>
        );
    }

    /* Render the Create User modal */
    function handleCloseCreate(): void
    {
        setShowCreate(false);
        resetCreateForm();
    }

    function handleToggleCreatePassword(): void
    {
        setShowCreatePassword(!showCreatePassword);
    }

    function renderCreateModal(): React.ReactNode
    {
        return (
            <Modal show={showCreate} onHide={handleCloseCreate} centered>
                {/* Custom header with blue plus icon */}
                <div className="um-modal-header">
                    <div className="um-modal-header-left">
                        <div className="um-modal-icon um-modal-icon-blue">
                            <PlusLg size={20} />
                        </div>
                        <div>
                            <h2 className="um-modal-title">Create New User</h2>
                            <p className="um-modal-subtitle">Add a new user to the system</p>
                        </div>
                    </div>
                    <button
                        className="um-modal-close"
                        onClick={handleCloseCreate}
                        type="button"
                        aria-label="Close"
                    >
                        <XLg size={18} />
                    </button>
                </div>

                <div className="um-modal-body">
                    {/* First Name / Last Name */}
                    <div className="row g-3 mb-3">
                        <div className="col-md-6">
                            <label className="um-edit-label">First Name</label>
                            <Form.Control
                                value={createForm.first_name}
                                onChange={(e) => setCreateForm({ ...createForm, first_name: e.target.value })}
                                isInvalid={!!createErrors.first_name}
                            />
                            <Form.Control.Feedback type="invalid">{createErrors.first_name}</Form.Control.Feedback>
                        </div>
                        <div className="col-md-6">
                            <label className="um-edit-label">Last Name</label>
                            <Form.Control
                                value={createForm.last_name}
                                onChange={(e) => setCreateForm({ ...createForm, last_name: e.target.value })}
                                isInvalid={!!createErrors.last_name}
                            />
                            <Form.Control.Feedback type="invalid">{createErrors.last_name}</Form.Control.Feedback>
                        </div>
                    </div>

                    {/* Email */}
                    <div className="mb-3">
                        <label className="um-edit-label">Email Address</label>
                        <Form.Control
                            type="email"
                            value={createForm.email}
                            onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                            isInvalid={!!createErrors.email}
                        />
                        <Form.Control.Feedback type="invalid">{createErrors.email}</Form.Control.Feedback>
                    </div>

                    {/* Organization */}
                    {isSuperAdmin && (
                        <Form.Check
                            className="mb-3"
                            type="checkbox"
                            label="Create as Super Admin"
                            checked={createForm.is_superadmin}
                            onChange={(e) => setCreateForm({
                                ...createForm,
                                is_superadmin: e.currentTarget.checked,
                                organization_ids: e.currentTarget.checked ? [] : createForm.organization_ids,
                                organization_roles: e.currentTarget.checked ? {} : createForm.organization_roles,
                            })}
                        />
                    )}

                    {!createForm.is_superadmin && <div className="mb-3">
                        <label className="um-edit-label">Organizations</label>
                        <OrganizationMultiSelect
                            options={organizationOptions}
                            selectedIds={createForm.organization_ids}
                            onChange={handleCreateOrganizationIdsChange}
                            placeholder="Select organizations"
                            invalid={!!createErrors.organization_ids}
                        />
                        <div className="form-text">Select one or more organizations from the dropdown.</div>
                        {createErrors.organization_ids && (
                            <div className="invalid-feedback d-block">{createErrors.organization_ids}</div>
                        )}
                        {renderOrganizationRoleRows(
                            createForm.organization_ids,
                            createForm.organization_roles,
                            handleCreateOrganizationRoleChange
                        )}
                    </div>}

                    {/* Temporary Password with HIDE/SHOW toggle + Auto-generate */}
                    <div className="mb-0">
                        <label className="um-edit-label">Temporary Password</label>
                        <div className="d-flex gap-2">
                            <div className="um-password-wrapper">
                                <Form.Control
                                    className="um-password-input"
                                    type={showCreatePassword ? 'text' : 'password'}
                                    value={createForm.temporary_password}
                                    onChange={(e) => setCreateForm({ ...createForm, temporary_password: e.target.value })}
                                    isInvalid={!!createErrors.temporary_password}
                                />
                                <button
                                    className="um-password-toggle"
                                    onClick={handleToggleCreatePassword}
                                    type="button"
                                >
                                    {showCreatePassword ? 'HIDE' : 'SHOW'}
                                </button>
                            </div>
                            <Button variant="outline-secondary" onClick={handleAutoGeneratePassword}>
                                Auto-generate
                            </Button>
                        </div>
                        <Form.Control.Feedback type="invalid">{createErrors.temporary_password}</Form.Control.Feedback>
                    </div>
                </div>

                {/* Footer buttons */}
                <div className="um-modal-footer">
                    <button
                        className="um-modal-btn-cancel"
                        onClick={handleCloseCreate}
                        disabled={saving}
                        type="button"
                    >
                        Cancel
                    </button>
                    <button
                        className="um-modal-btn-save um-modal-btn-primary"
                        onClick={handleCreate}
                        disabled={saving}
                        type="button"
                    >
                        {saving ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" className="me-2" />
                                Creating...
                            </>
                        ) : 'Create User'}
                    </button>
                </div>
            </Modal>
        );
    }

    /* Render the Edit User modal */
    function renderEditModal(): React.ReactNode
    {
        if (!editTarget)
        {
            return null;
        }

        const initials = getInitials(editTarget.first_name, editTarget.last_name);
        const avatarColor = getAvatarColor(editTarget.id);
        const fullName = `${editTarget.first_name} ${editTarget.last_name}`.trim();

        return (
            <Modal show={!!editTarget} onHide={() => { setEditTarget(null); }} centered>
                {/* Custom header with blue pencil icon */}
                <div className="um-modal-header">
                    <div className="um-modal-header-left">
                        <div className="um-modal-icon um-modal-icon-blue">
                            <PencilSquare size={20} />
                        </div>
                        <div>
                            <h2 className="um-modal-title">Edit User</h2>
                            <p className="um-modal-subtitle">Update profile and permissions</p>
                        </div>
                    </div>
                    <button
                        className="um-modal-close"
                        onClick={() => { setEditTarget(null); }}
                        type="button"
                        aria-label="Close"
                    >
                        <XLg size={18} />
                    </button>
                </div>

                <div className="um-modal-body">
                    {/* User info card */}
                    <div className="um-edit-user-card">
                        <div className={`um-avatar ${avatarColor}`}>
                            {initials}
                        </div>
                        <div>
                            <div className="um-user-name">{fullName}</div>
                            <div className="um-user-email">{editTarget.email}</div>
                        </div>
                    </div>

                    {/* First Name / Last Name */}
                    <div className="row g-3 mb-3">
                        <div className="col-md-6">
                            <label className="um-edit-label">First Name</label>
                            <Form.Control
                                value={editTarget.first_name}
                                onChange={(e) => setEditTarget({ ...editTarget, first_name: e.target.value })}
                            />
                        </div>
                        <div className="col-md-6">
                            <label className="um-edit-label">Last Name</label>
                            <Form.Control
                                value={editTarget.last_name}
                                onChange={(e) => setEditTarget({ ...editTarget, last_name: e.target.value })}
                            />
                        </div>
                    </div>

                    {/* Email */}
                    <div className="mb-3">
                        <label className="um-edit-label">Email</label>
                        <Form.Control
                            type="email"
                            value={editTarget.email}
                            disabled
                            readOnly
                        />
                    </div>

                    {/* Organization */}
                    {!isSuperAdminUser(editTarget) && (
                        <div className="mb-3">
                            <label className="um-edit-label">Organizations</label>
                            <OrganizationMultiSelect
                                options={organizationOptions}
                                selectedIds={getUserOrganizationIds(editTarget)}
                                onChange={handleEditOrganizationIdsChange}
                                placeholder="Select organizations"
                            />
                            <div className="form-text">Select one or more organizations from the dropdown.</div>
                            {renderOrganizationRoleRows(
                                getUserOrganizationIds(editTarget),
                                editTarget.organization_roles || getUserOrganizationRoleMap(editTarget),
                                handleEditOrganizationRoleChange
                            )}
                        </div>
                    )}

                    {/* Password (read-only) */}
                    <div className="mb-0">
                        <label className="um-edit-label">Password</label>
                        <Form.Control
                            className="um-password-input"
                            type="password"
                            value="********"
                            disabled
                            readOnly
                        />
                    </div>
                </div>

                {/* Footer buttons */}
                <div className="um-modal-footer">
                    <button
                        className="um-modal-btn-cancel"
                        onClick={() => { setEditTarget(null); }}
                        disabled={saving}
                        type="button"
                    >
                        Cancel
                    </button>
                    <button
                        className="um-modal-btn-save um-modal-btn-primary"
                        onClick={handleSaveEdit}
                        disabled={saving}
                        type="button"
                    >
                        {saving ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" className="me-2" />
                                Saving...
                            </>
                        ) : 'Save Changes'}
                    </button>
                </div>
            </Modal>
        );
    }

    /* Render the Remove User modal */
    function renderDeleteModal(): React.ReactNode
    {
        if (!deleteTarget)
        {
            return null;
        }

        const initials = getInitials(deleteTarget.first_name, deleteTarget.last_name);
        const fullName = `${deleteTarget.first_name} ${deleteTarget.last_name}`.trim();
        const isSelf = user?.email?.toLowerCase() === deleteTarget.email?.toLowerCase();
        const soleAdminOrganizations = selfDeletePreview?.organizations_to_delete || [];
        const soleAdminOrganizationNames = soleAdminOrganizations
            .map((organization) => organization.organization_name)
            .join(', ');
        const sharedOrganizationNames = selfDeletePreview?.organizations_to_remove_from
            .map((organization) => organization.organization_name)
            .join(', ');
        const selfDeleteBlockedByJobs = Boolean(selfDeletePreview?.running_jobs.length);

        return (
            <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)} centered>
                {/* Custom header with red trash icon */}
                <div className="um-modal-header">
                    <div className="um-modal-header-left">
                        <div className="um-modal-icon um-modal-icon-red">
                            <Trash3 size={20} />
                        </div>
                        <div>
                            <h2 className="um-modal-title">{isSelf ? 'Delete My Account' : 'Remove User'}</h2>
                            <p className="um-modal-subtitle">Review the impact before continuing.</p>
                        </div>
                    </div>
                    <button
                        className="um-modal-close"
                        onClick={() => setDeleteTarget(null)}
                        type="button"
                        aria-label="Close"
                    >
                        <XLg size={18} />
                    </button>
                </div>

                <div className="um-modal-body">
                    {/* Label */}
                    <div className="um-delete-label">
                        {isSelf ? 'You are about to delete your account' : 'You are about to remove this user'}
                    </div>

                    {/* User info card */}
                    <div className="um-delete-user-card">
                        <div className={`um-avatar ${getAvatarColor(deleteTarget.id)}`}>
                            {initials}
                        </div>
                        <div>
                            <div className="um-user-name">{fullName}</div>
                            <div className="um-user-email">{deleteTarget.email}</div>
                        </div>
                    </div>

                    {/* Yellow warning box */}
                    <div className="um-warning-box">
                        <ExclamationTriangleFill className="um-warning-icon" />
                        {isSelf ? (
                            <ul>
                                {selfDeletePreviewLoading ? (
                                    <li>Checking your organization and job impact...</li>
                                ) : (
                                    <>
                                        {soleAdminOrganizationNames && (
                                            <li>
                                                {soleAdminOrganizations.length === 1
                                                    ? `You are the only admin of ${soleAdminOrganizationNames}. This organization and its related data will be deleted.`
                                                    : `You are the only admin of ${soleAdminOrganizationNames}. These organizations and their related data will be deleted.`}
                                            </li>
                                        )}
                                        {sharedOrganizationNames && (
                                            <li>
                                                You will only be removed from {sharedOrganizationNames}; those organizations will remain available to their other admins.
                                            </li>
                                        )}
                                        {selfDeleteBlockedByJobs && (
                                            <li>
                                                Active jobs are running in this organization. Cancel or stop job(s) {selfDeletePreview?.running_jobs.map((job) => `#${job.job_id}`).join(', ')} before deleting your account.
                                            </li>
                                        )}
                                    </>
                                )}
                                <li>Your account will be deactivated and you will be signed out.</li>
                            </ul>
                        ) : (
                            <ul>
                                <li>The account will be deactivated.</li>
                                <li>The user will no longer be able to sign in.</li>
                                <li>Users cannot delete their own account from this action.</li>
                            </ul>
                        )}
                    </div>
                </div>

                {/* Footer buttons */}
                <div className="um-modal-footer">
                    <button
                        className="um-modal-btn-cancel"
                        onClick={() => setDeleteTarget(null)}
                        disabled={saving}
                        type="button"
                    >
                        Cancel
                    </button>
                    <button
                        className="um-modal-btn-save"
                        onClick={handleConfirmDelete}
                        disabled={saving || (isSelf && (selfDeletePreviewLoading || !selfDeletePreview || selfDeleteBlockedByJobs))}
                        type="button"
                    >
                        {saving ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" className="me-2" />
                                Deleting...
                            </>
                        ) : isSelf ? 'Yes, Delete My Account' : 'Yes, Remove User'}
                    </button>
                </div>
            </Modal>
        );
    }

    /* Render the Bulk Delete Confirmation modal */
    function renderBulkDeleteModal(): React.ReactNode
    {
        /* Resolve selected users from the current items list */
        const selectedUsers = items.filter((u) => selectedIds.includes(u.id));

        return (
            <Modal show={showBulkDeleteModal} onHide={() => setShowBulkDeleteModal(false)} centered>
                {/* Custom header with red trash icon */}
                <div className="um-modal-header">
                    <div className="um-modal-header-left">
                        <div className="um-modal-icon um-modal-icon-red">
                            <Trash3 size={20} />
                        </div>
                        <div>
                            <h2 className="um-modal-title">Confirm Removing Users</h2>
                            <p className="um-modal-subtitle">This will permanently remove from your workspace.</p>
                        </div>
                    </div>
                    <button
                        className="um-modal-close"
                        onClick={() => setShowBulkDeleteModal(false)}
                        type="button"
                        aria-label="Close"
                    >
                        <XLg size={18} />
                    </button>
                </div>

                <div className="um-modal-body">
                    {/* Pink box listing users */}
                    <div className="um-bulk-delete-box">
                        <div className="um-bulk-delete-box-title">
                            The following {selectedIds.length} users will be removed:
                        </div>
                        <ul>
                            {selectedUsers.map((u) => (
                                <li key={u.id}>
                                    <strong>{u.first_name} {u.last_name}</strong>
                                    <span>({u.email})</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Footer buttons */}
                <div className="um-modal-footer">
                    <button
                        className="um-modal-btn-cancel"
                        onClick={() => setShowBulkDeleteModal(false)}
                        disabled={bulkRunning}
                        type="button"
                    >
                        Cancel
                    </button>
                    <button
                        className="um-modal-btn-save"
                        onClick={handleConfirmBulkDelete}
                        disabled={bulkRunning}
                        type="button"
                    >
                        {bulkRunning ? 'Deleting...' : `Yes, Delete ${selectedIds.length} Users`}
                    </button>
                </div>
            </Modal>
        );
    }

    /* ---------- Main render ---------- */

    return (
        <div className="um-page">
            {/* Notification banner */}
            {renderNotification()}

            {/* Error banner */}
            {error && (
                <div className="um-alert um-alert-danger">
                    <span>{error}</span>
                    <button className="um-alert-dismiss" onClick={() => setError(null)} type="button">
                        Dismiss
                    </button>
                </div>
            )}

            {/* Page header: title + action buttons */}
            {renderHeader()}

            {/* Delete mode warning banner */}
            {renderDeleteBanner()}

            {/* Search / filter bar */}
            {renderFilters()}

            {/* Users table card */}
            <div className="um-table-card">
                <table className="um-table">
                    {renderTableHead()}
                    {renderTableBody()}
                </table>

                {renderMobileUserList()}

                {/* Footer with pagination */}
                {renderFooter()}
            </div>

            {/* Modals */}
            {renderCreateModal()}
            {renderEditModal()}
            {renderDeleteModal()}
            {renderBulkDeleteModal()}
        </div>
    );
};

export default UserManagement;
